import { useEffect, useMemo, useState } from "react"
import { NavLink, useNavigate } from "react-router"
import { ToastMessage, useToast } from "@stayframe/ui"
import { formatApiError } from "@/lib/api"
import { opsAdminApi, type OpsPropertyFilter } from "@/lib/opsAdminApi"
import { OpsSummaryCards } from "@/components/ops-admin/OpsSummaryCards"
import { OpsGlobalSearch } from "@/components/ops-admin/OpsGlobalSearch"
import { OpsPropertiesTable } from "@/components/ops-admin/OpsPropertiesTable"
import { OpsPropertyDrawer } from "@/components/ops-admin/OpsPropertyDrawer"
import { OpsJobRunsCard } from "@/components/ops-admin/OpsJobRunsCard"
import { OpsErrorsCard } from "@/components/ops-admin/OpsErrorsCard"
import { OpsFeedbackCard } from "@/components/ops-admin/OpsFeedbackCard"
import { OpsIntelligenceGateCard } from "@/components/ops-admin/OpsIntelligenceGateCard"
import { OpsSubscriptionsCard } from "@/components/ops-admin/OpsSubscriptionsCard"
import { OpsAiHealthCard } from "@/components/ops-admin/OpsAiHealthCard"
import { AiCcExpensesCard } from "@/components/ai-control-center/AiCcExpensesCard"

interface SummaryData {
  properties: { total: number; active: number }
  devices: { seen_24h: number; stale_7d: number; never_seen: number }
  jobs: { last_run_ok: number; failures_24h: number }
  guidebook_sessions?: { logins_24h: number; failed_logins_24h: number } | null
  llm_usage?: { today_usd: number; last_7d_usd: number } | null
}

interface SearchResults {
  properties?: Array<{ id: string; name: string; slug: string; host_email: string }>
  users?: Array<{ id: string; email: string; name: string; role: string; is_active: boolean }>
  devices?: Array<{ id: string; token_hash: string; property_id: string; last_seen_at: string; created_at: string }>
  bookings?: Array<{ id: string; property_id: string; property_name: string; booking_number: string; start_date: string; end_date: string; summary: string }>
}

interface OpsPropertyRow {
  property_id: string
  property_name: string
  property_short_id: string
  host_email: string
  pms_health: { status: string; provider: string; connected: boolean }
  calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
  places_health: { status: string; count: number }
  display_health: { status: string; last_heartbeat: string; device_id: string }
  guidebook_health: { status: string; enabled: boolean; last_login_at: string }
  last_render_at: string
  open_issues: string[]
  issue_count: number
}

interface OpsPropertyDetail {
  property_id: string
  property_name: string
  host_email: string
  pms_health: { status: string; provider: string; connected: boolean }
  calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
  places_health: { status: string; count: number }
  display_health: { status: string; last_heartbeat: string; device_id: string }
  guidebook_health: { status: string; enabled: boolean; last_login_at: string }
  controls: { updates_disabled: boolean; llm_disabled: boolean; updated_at: string; updated_by: string }
  key_ids: {
    property_id: string
    primary_listing_id: string
    primary_listing_name: string
    render_key: string
    pms_property_ref: string
  }
  latest_activity: Array<{ ts: string; type: string; label: string; status: string; message: string }>
}

interface OpsJobRun {
  id: string
  time: string
  job_type: string
  property_id: string
  property_name: string
  status: string
  duration_ms: number
  error_snippet: string
  retryable: boolean
}

interface OpsErrorRow {
  time: string
  level: string
  scope: string
  property_id: string
  category: string
  message: string
  correlation_id: string
}

export function OpsAdminPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [properties, setProperties] = useState<OpsPropertyRow[]>([])
  const [propertyFilter, setPropertyFilter] = useState<OpsPropertyFilter>("all")
  const [propertiesLoading, setPropertiesLoading] = useState(true)

  const [searchQuery, setSearchQuery] = useState("")
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null)

  const [selectedPropertyId, setSelectedPropertyId] = useState("")
  const [selectedProperty, setSelectedProperty] = useState<OpsPropertyDetail | null>(null)
  const [propertyLoading, setPropertyLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState("")

  const [jobRuns, setJobRuns] = useState<OpsJobRun[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobsFailedOnly, setJobsFailedOnly] = useState(true)
  const [jobsType, setJobsType] = useState("")
  const [retryingJobId, setRetryingJobId] = useState("")

  const [errors, setErrors] = useState<OpsErrorRow[]>([])
  const [errorsLoading, setErrorsLoading] = useState(true)

  const jobTypeOptions = useMemo(() => {
    const discovered = Array.from(new Set(jobRuns.map((row) => row.job_type).filter(Boolean))).sort()
    return jobsType && !discovered.includes(jobsType) ? [jobsType, ...discovered] : discovered
  }, [jobRuns, jobsType])

  const propertyOptions = useMemo(() => {
    const seen = new Set<string>()
    const rows: Array<{ id: string; name: string }> = []
    for (const row of properties) {
      const id = String(row.property_id || "").trim()
      if (!id || seen.has(id)) continue
      seen.add(id)
      rows.push({
        id,
        name: String(row.property_name || id).trim() || id,
      })
    }
    rows.sort((a, b) => a.name.localeCompare(b.name))
    return rows
  }, [properties])



  async function copyText(value: string) {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      toast("Copied to clipboard.", "success")
    } catch {
      toast("Failed to copy to clipboard.", "error")
    }
  }

  function showApiError(result: Record<string, unknown>, fallback = "Request failed.") {
    const error = formatApiError(result)
    toast(<ToastMessage message={error.message || fallback} requestId={error.requestId} />, "error")
  }

  async function loadSummary() {
    const res = await opsAdminApi.getSummary()
    if (res.ok && res.summary) {
      setSummary(res.summary)
      return
    }
    showApiError(res as unknown as Record<string, unknown>, "Failed to load summary.")
  }

  async function loadProperties(nextFilter: OpsPropertyFilter = propertyFilter) {
    setPropertiesLoading(true)
    const res = await opsAdminApi.getProperties(nextFilter)
    if (res.ok && res.properties) {
      setProperties(res.properties as OpsPropertyRow[])
    } else {
      setProperties([])
      showApiError(res as unknown as Record<string, unknown>, "Failed to load properties.")
    }
    setPropertiesLoading(false)
  }

  async function loadPropertyDetail(propertyId: string) {
    if (!propertyId) return
    setSelectedPropertyId(propertyId)
    setPropertyLoading(true)
    const res = await opsAdminApi.getProperty(propertyId)
    if (res.ok && res.property) {
      setSelectedProperty(res.property as OpsPropertyDetail)
    } else {
      setSelectedProperty(null)
      showApiError(res as unknown as Record<string, unknown>, "Failed to load property detail.")
    }
    setPropertyLoading(false)
  }

  async function loadJobs(next: { failedOnly?: boolean; type?: string } = {}) {
    const failedOnly = next.failedOnly ?? jobsFailedOnly
    const type = next.type ?? jobsType
    setJobsLoading(true)
    const res = await opsAdminApi.getJobRuns({ failedOnly, type, limit: 20 })
    if (res.ok && res.job_runs) {
      setJobRuns(res.job_runs as OpsJobRun[])
    } else {
      setJobRuns([])
      showApiError(res as unknown as Record<string, unknown>, "Failed to load job runs.")
    }
    setJobsLoading(false)
  }

  async function loadErrors() {
    setErrorsLoading(true)
    const res = await opsAdminApi.getErrors(30)
    if (res.ok && res.errors) {
      setErrors(res.errors as OpsErrorRow[])
    } else {
      setErrors([])
      showApiError(res as unknown as Record<string, unknown>, "Failed to load recent errors.")
    }
    setErrorsLoading(false)
  }

  async function loadAll() {
    await Promise.all([
      loadSummary(),
      loadProperties(propertyFilter),
      loadJobs({ failedOnly: jobsFailedOnly, type: jobsType }),
      loadErrors(),
    ])
  }

  async function handleSearch() {
    const q = searchQuery.trim()
    if (!q) {
      setSearchResults(null)
      return
    }
    setSearching(true)
    const res = await opsAdminApi.search(q)
    if (res.ok) {
      setSearchResults({
        properties: res.properties || [],
        users: res.users || [],
        devices: res.devices || [],
        bookings: res.bookings || [],
      })
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Search failed.")
    }
    setSearching(false)
  }

  async function handlePropertyAction(actionType: string, payload?: Record<string, unknown>) {
    if (!selectedPropertyId) return
    setActionBusy(actionType)
    const res = await opsAdminApi.runPropertyAction(selectedPropertyId, actionType, payload)
    if (res.ok) {
      toast("Action completed.", "success")
      await Promise.all([
        loadSummary(),
        loadProperties(propertyFilter),
        loadPropertyDetail(selectedPropertyId),
        loadJobs({ failedOnly: jobsFailedOnly, type: jobsType }),
        loadErrors(),
      ])
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Action failed.")
    }
    setActionBusy("")
  }

  async function handleRetryJob(jobId: string) {
    if (!jobId) return
    setRetryingJobId(jobId)
    const res = await opsAdminApi.retryJob(jobId)
    if (res.ok) {
      toast("Retry queued.", "success")
      await loadJobs({ failedOnly: jobsFailedOnly, type: jobsType })
    } else {
      showApiError(res as unknown as Record<string, unknown>, "Retry failed.")
    }
    setRetryingJobId("")
  }

  useEffect(() => {
    loadAll().catch(() => {})
  }, [])

  return (
    <div className="ops-admin-page">
      <div className="page-header ops-page-header">
        <div className="ops-page-header__row">
          <h1 className="page-header__title" style={{ marginBottom: 0 }}>Ops Admin</h1>
          <span className="ops-badge">Ops</span>
        </div>
        <p className="page-header__subtitle">
          Fleet operations cockpit for diagnostics, safety controls, and cross-property actions.
        </p>
        <button type="button" className="data-table__action" onClick={() => loadAll()}>
          Refresh
        </button>
      </div>

      <OpsSummaryCards
        summary={summary}
        activeFilter={propertyFilter}
        onFilterChange={(next) => {
          setPropertyFilter(next)
          loadProperties(next).catch(() => {})
        }}
      />

      <NavLink to="/admin/ai" className="ai-cc-link-card">
        <div className="ai-cc-link-card__icon">✦</div>
        <div className="ai-cc-link-card__text">
          <div className="ai-cc-link-card__title">AI Control Center</div>
          <div className="ai-cc-link-card__desc">Cost intelligence, unit economics, model performance, and operational controls</div>
        </div>
        <div className="ai-cc-link-card__arrow">→</div>
      </NavLink>

      <OpsIntelligenceGateCard />

      <OpsSubscriptionsCard />

      <OpsAiHealthCard
        propertyOptions={propertyOptions}
        onApiError={showApiError}
        onOpenProperty={(propertyId) => loadPropertyDetail(propertyId).catch(() => {})}
      />

      <AiCcExpensesCard />

      <OpsGlobalSearch
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onSubmit={handleSearch}
        isSearching={searching}
        results={searchResults}
        onOpenProperty={(propertyId) => navigate(`/properties/${propertyId}`)}
        onOpenDevice={(propertyId) => {
          navigate(`/properties/${propertyId}/settings`)
        }}
        onViewLogs={(propertyId) => navigate(`/properties/${propertyId}/jobs`)}
        onCopy={(value) => copyText(value).catch(() => {})}
      />

      <OpsPropertiesTable
        rows={properties}
        loading={propertiesLoading}
        filter={propertyFilter}
        selectedPropertyId={selectedPropertyId}
        onFilterChange={(next) => {
          setPropertyFilter(next)
          loadProperties(next).catch(() => {})
        }}
        onSelectProperty={(propertyId) => {
          loadPropertyDetail(propertyId).catch(() => {})
        }}
      />

      <div className="ops-bottom-grid">
        <OpsJobRunsCard
          rows={jobRuns}
          loading={jobsLoading}
          failedOnly={jobsFailedOnly}
          type={jobsType}
          typeOptions={jobTypeOptions}
          retryingJobId={retryingJobId}
          onFailedOnlyChange={(value) => {
            setJobsFailedOnly(value)
            loadJobs({ failedOnly: value, type: jobsType }).catch(() => {})
          }}
          onTypeChange={(value) => {
            setJobsType(value)
            loadJobs({ failedOnly: jobsFailedOnly, type: value }).catch(() => {})
          }}
          onRetry={(jobId) => handleRetryJob(jobId).catch(() => {})}
          onOpenProperty={(propertyId) => loadPropertyDetail(propertyId).catch(() => {})}
        />

        <OpsErrorsCard
          rows={errors}
          loading={errorsLoading}
          onCopy={(value) => copyText(value).catch(() => {})}
          onOpenProperty={(propertyId) => loadPropertyDetail(propertyId).catch(() => {})}
        />
      </div>

      <OpsFeedbackCard />

      <OpsPropertyDrawer
        property={selectedProperty}
        isLoading={propertyLoading}
        actionBusy={actionBusy}
        onClose={() => {
          setSelectedPropertyId("")
          setSelectedProperty(null)
          setPropertyLoading(false)
        }}
        onAction={(actionType, payload) => {
          handlePropertyAction(actionType, payload).catch(() => {})
        }}
        onCopy={(value) => copyText(value).catch(() => {})}
      />
    </div>
  )
}
